import { FC, useEffect, useState } from "react";
import {
    Box,
    Button,
    FormControl,
    MenuItem,
    Modal,
    Select,
    Typography,
} from "@mui/material";
import { useSingleUserControl } from "../../../context/SuperAdmin/SingleUserControlContext";
import { getAllSuscriptions, postUsersInSuscription } from "../../../api/broker";
import { toast } from "react-toastify";
import { iDataSuscripcionOrg } from "../../../interfaces/SuperAdmin/Pages/iDataSuscripcion";

interface Props {
    isOpen: boolean;
    setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
    organizationId: string;
    organizationName: string;
}

const AddSubscriptionToOrgModal: FC<Props> = ({
    isOpen,
    setIsOpen,
    organizationId,
    organizationName,
}) => {
    const { rdsUser, refreshUserData } = useSingleUserControl();
    const [subscriptions, setSubscriptions] = useState<iDataSuscripcionOrg[]>([]);
    const [selectedSuscripcion, setSelectedSuscripcion] = useState<string>("");
    const [isLoading, setIsLoading] = useState(false);
    const [isAdding, setIsAdding] = useState(false);

    const handleClose = () => {
        if (!isAdding) {
            setIsOpen(false);
            setSelectedSuscripcion("");
        }
    };

    // Fetch subscriptions for this organization
    useEffect(() => {
        const fetchSuscriptions = async () => {
            if (isOpen && organizationId) {
                setIsLoading(true);
                try {
                    const res = await getAllSuscriptions(organizationId);
                    if (res.data.statusCode === 200) {
                        setSubscriptions(res.data.body);
                    } else {
                        toast.error("Error al cargar suscripciones");
                    }
                } catch (error) {
                    console.error(error);
                    toast.error("Error al cargar suscripciones");
                } finally {
                    setIsLoading(false);
                }
            }
        };

        fetchSuscriptions();
    }, [isOpen, organizationId]);

    const handleAdd = async () => {
        if (!rdsUser || !selectedSuscripcion) return;

        setIsAdding(true);
        try {
            const res = await postUsersInSuscription({
                id_usuarios: [rdsUser.id_usuario],
                id_suscripcion: selectedSuscripcion,
                id_organizacion: organizationId,
            });

            if (res.data.statusCode === 201) {
                toast.success("Suscripción agregada exitosamente");
                await refreshUserData();
                handleClose();
            } else {
                toast.error("Error al agregar suscripción");
            }
        } catch (error) {
            console.error(error);
            toast.error("Error al agregar suscripción");
        } finally {
            setIsAdding(false);
        }
    };

    return (
        <Modal
            open={isOpen}
            onClose={handleClose}
            sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "rgba(0, 0, 0, 0.3)",
            }}
        >
            <Box
                sx={{
                    width: 400,
                    bgcolor: "background.paper",
                    borderRadius: 2,
                    p: 4,
                    boxShadow: 24,
                    minWidth: "500px",
                    display: "flex",
                    flexDirection: "column",
                    gap: 3,
                }}
            >
                <Typography variant="h6" component="h2">
                    Agregar Suscripción a {organizationName}
                </Typography>

                {isLoading ? (
                    <Typography>Cargando suscripciones...</Typography>
                ) : (
                    <>
                        <Box>
                            <Typography variant="body1" sx={{ mb: 1, fontWeight: 500 }}>
                                Seleccionar Suscripción
                            </Typography>
                            <FormControl fullWidth>
                                <Select
                                    value={selectedSuscripcion}
                                    onChange={(e) => setSelectedSuscripcion(e.target.value)}
                                    disabled={isAdding}
                                    displayEmpty
                                >
                                    <MenuItem value="" disabled>
                                        Seleccione una suscripción
                                    </MenuItem>
                                    {subscriptions.map((sus) => (
                                        <MenuItem key={sus.id_suscripcion} value={sus.id_suscripcion}>
                                            {sus.plan} - Vence: {sus.fecha_fin}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>

                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "flex-end",
                                gap: 2,
                                mt: 2,
                            }}
                        >
                            <Button
                                variant="outlined"
                                onClick={handleClose}
                                disabled={isAdding}
                                sx={{
                                    borderRadius: 14,
                                    border: "1px solid #336B87",
                                    color: "#336B87",
                                    "&:hover": {
                                        backgroundColor: "#336B87",
                                        color: "#EEF3F6",
                                    },
                                }}
                            >
                                Cancelar
                            </Button>
                            <Button
                                variant="contained"
                                sx={{ background: "#336B87", borderRadius: 14 }}
                                disabled={isAdding || !selectedSuscripcion}
                                onClick={handleAdd}
                            >
                                {isAdding ? "Agregando..." : "Agregar"}
                            </Button>
                        </Box>
                    </>
                )}
            </Box>
        </Modal>
    );
};

export default AddSubscriptionToOrgModal;
